import React, { useEffect, useRef } from 'react';
import './TextBackground.css';

// Global state to track if loading animation has been completed
let hasLoadedOnce = false;

const TextBackground = () => {
  const canvasRef = useRef(null);
  const animationRef = useRef(null);
  
  // Loading animation state
  const isLoading = useRef(!hasLoadedOnce);
  const loadingProgress = useRef(hasLoadedOnce ? 100 : 0);
  const loadingSpeed = 0.08; // How fast the trickle effect progresses

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    
    // Set canvas size to match window
    const resizeCanvas = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    
    resizeCanvas();
    window.addEventListener('resize', resizeCanvas);

     // Lorem ipsum text - repeating to fill the background
     const loremText = `Lorem ipsum dolor sit amet, consectetur adipiscing elit. Sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation ullamco laboris nisi ut aliquip ex ea commodo consequat. Duis aute irure dolor in reprehenderit in voluptate velit esse cillum dolore eu fugiat nulla pariatur. Excepteur sint occaecat cupidatat non proident, sunt in culpa qui officia deserunt mollit anim id est laborum. Sed ut perspiciatis unde omnis iste natus error sit voluptatem accusantium doloremque laudantium, totam rem aperiam, eaque ipsa quae ab illo inventore veritatis et quasi architecto beatae vitae dicta sunt explicabo. Nemo enim ipsam voluptatem quia voluptas sit aspernatur aut odit aut fugit, sed quia consequuntur magni dolores eos qui ratione voluptatem sequi nesciunt. Neque porro quisquam est, qui dolorem ipsum quia dolor sit amet, consectetur, adipisci velit, sed quia non numquam eius modi tempora incididunt ut labore et dolore magnam aliquam quaerat voluptatem. Ut enim ad minima veniam, quis nostrum exercitationem ullam corporis suscipit laboriosam, nisi ut aliquid ex ea commodi consequatur? Quis autem vel eum iure reprehenderit qui in ea voluptate velit esse quam nihil molestiae consequatur, vel illum qui dolorem eum fugiat quo voluptas nulla pariatur?`;
     
     // Set up grid data structure
     const GRID_WIDTH = 10;
     const rows = Math.ceil(canvas.height / GRID_WIDTH);
     const columns = Math.ceil(canvas.width / GRID_WIDTH);
     
     
    
    // Create grid data to track which characters to animate
    const gridData = [];
    for (let i = 0; i < columns; i++) {
      gridData[i] = [];
      for (let j = 0; j < rows; j++) {
        const textIndex = (j * columns + i) % loremText.length;
        gridData[i][j] = {
          char: loremText[textIndex],
          originalChar: loremText[textIndex],
          isFlipped: false,
          flipDuration: 0,
          slideProgress: 0,
          isSliding: false,
          slideDuration: 0
        };
      }
    }

    // Function to draw text background with animation
    const drawTextBackground = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      
      // Set text properties
      ctx.font = `${GRID_WIDTH}px Arial`;
      ctx.textAlign = 'left';
      ctx.textBaseline = 'top';
      
      // Draw all characters
      for (let i = 0; i < columns; i++) {
        for (let j = 0; j < rows; j++) {
          const x = GRID_WIDTH * i;
          const y = GRID_WIDTH * j;
          const cell = gridData[i][j];
          
          // Update slide animation
          if (cell.isSliding) {
            cell.slideDuration += 0.016; // ~60fps timing
            cell.slideProgress = Math.min(cell.slideDuration / 0.3, 1); // 0.3 second slide duration
            
            if (cell.slideProgress >= 1) {
              cell.isSliding = false;
              cell.slideDuration = 0;
              cell.slideProgress = 0;
            }
          }
          
          
          // Update flip animation
          if (cell.isFlipped) {
            cell.flipDuration += 0.016; // ~60fps timing
            if (cell.flipDuration >= cell.maxFlipDuration) {
              cell.isFlipped = false;
              cell.flipDuration = 0;
              cell.char = cell.originalChar;
            }
          }
          
          
          
          // Check if this character should be visible during loading
          let shouldDraw = true;
          let opacity = 1;
          
          if (isLoading.current) {
            // Calculate trickle-down effect based on row position
            const rowProgress = (j / rows) * 100; // Percentage of screen height
            const trickleDelay = rowProgress * 0.8; // Delay based on row position
            
            // Only show characters that have "trickled down" to this row
            if (loadingProgress.current < trickleDelay) {
              shouldDraw = false;
            } else {
              // Fade in effect for characters that just appeared
              const fadeProgress = Math.min(1, (loadingProgress.current - trickleDelay) / 10);
              opacity = fadeProgress;
            }
          }
          
          // Draw character with slide effect
          if (shouldDraw) {
            if (cell.isSliding) {
              // Easing function for smooth slide (ease-out)
              const easeOut = 1 - Math.pow(1 - cell.slideProgress, 3);
              const slideOffset = GRID_WIDTH * (1 - easeOut);
              
              // Draw sliding character with loading opacity
              ctx.fillStyle = `rgba(255, 255, 255, ${opacity})`;
              ctx.fillText(cell.char, x, y - slideOffset);
            } else {
              // Draw normal character with loading opacity
              ctx.fillStyle = `rgba(255, 255, 255, ${opacity})`;
              ctx.fillText(cell.char, x, y);
            }
          }
        }
      }
    };

    // Animation loop
    const animate = () => {
      // Update loading animation
      if (isLoading.current) {
        loadingProgress.current += loadingSpeed;
        
        // Check if loading is complete
        if (loadingProgress.current >= 100) {
          isLoading.current = false;
          loadingProgress.current = 100;
          hasLoadedOnce = true; // Mark that loading has completed globally
        }
      }
      
      drawTextBackground();
      
      // Randomly start character flip animations
      if (Math.random() < 0.03) { // 3% chance per frame for flip effect
        // Pick a random starting position
        const randomCol = Math.floor(Math.random() * columns);
        const randomRow = Math.floor(Math.random() * rows);
        
        // Random sequence length (1-10 characters)
        const sequenceLength = Math.floor(Math.random() * 10) + 1;
        
        // Check if we can fit the sequence horizontally
        if (randomCol + sequenceLength <= columns) {
          // Check if all positions are available
          let canPlace = true;
          for (let i = 0; i < sequenceLength; i++) {
            const col = randomCol + i;
            const row = randomRow;
            if (gridData[col][row].isFlipped || gridData[col][row].isSliding) {
              canPlace = false;
              break;
            }
          }
          
          // Place the sequence if all positions are available
          if (canPlace) {
            for (let i = 0; i < sequenceLength; i++) {
              const col = randomCol + i;
              const row = randomRow;
              const cell = gridData[col][row];
              
              // Generate a random character (letters and numbers)
              const randomChars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
              cell.char = randomChars[Math.floor(Math.random() * randomChars.length)];
              cell.isFlipped = true;
              cell.flipDuration = 0;
              cell.maxFlipDuration = 5; // 5 seconds
              cell.isSliding = true;
              cell.slideDuration = 0;
              cell.slideProgress = 0;
            }
          }
        }
      }
      
      animationRef.current = requestAnimationFrame(animate);
    };

    // Start animation - only delay if it's the first load
    if (hasLoadedOnce) {
      animate(); // Start immediately if already loaded once
    } else {
      setTimeout(() => {
        animate();
      }, 500); // 500ms delay only for first load
    }

    return () => {
      window.removeEventListener('resize', resizeCanvas);
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      className="text-background"
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        zIndex: 0,
        pointerEvents: 'none'
      }}
    />
  );
};

export default TextBackground;
